import React, { useRef } from "react";
import { View, TextInput } from "react-native";
import { Typography } from "../Typography";
import { RadioButtonGroup, SelectType } from "../form/RadioButtonGroup";
import colors from "../../styles/colors";
import { IconButton } from "../buttons/IconButton";
import ClipboardIcon from "../../assets/icons/clipboard.svg";
import { getClipboard } from "../../clipboard/clipboard";
import { useTranslation } from "react-i18next";

interface InputMethodSelectionProps {
  selectedInputMethod: InputMethod;
  onInputMethodChange: (method: InputMethod) => void;
  urlInput: string;
  onUrlChange: (urlInput: string) => void;
  noCaption?: boolean;
}

export enum InputMethod {
  Input = "input",
  Url = "url",
  Files = "files",
}

export const InputMethodSelection: React.FC<InputMethodSelectionProps> = ({
  selectedInputMethod,
  onInputMethodChange,
  urlInput,
  onUrlChange,
  noCaption,
}) => {
  const { t } = useTranslation();
  const inputRef = useRef<TextInput>(null);

  const selectValues: SelectType<InputMethod>[] = [
    {
      label: t("propertyCreation.methods.enterInfo"),
      value: InputMethod.Input,
    },
    {
      label: t("propertyCreation.methods.uploadLink"),
      value: InputMethod.Url,
      contentUnderRadioButton: (
        <View
          style={{
            borderColor: colors.greyborder,
            borderWidth: 1,
            borderRadius: 5,
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
            marginTop: 15,
            opacity: selectedInputMethod !== InputMethod.Url ? 0.2 : 1,
          }}
          pointerEvents={
            selectedInputMethod !== InputMethod.Url ? "none" : "auto"
          }
        >
          <TextInput
            ref={inputRef}
            value={urlInput}
            onChangeText={(text) => {
              onUrlChange(text);
            }}
            placeholder={t("propertyCreation.methods.linkPlaceholder")}
            style={{ padding: 12, flex: 1 }}
            autoCapitalize="none"
            autoCorrect={false}
          />
          <IconButton
            onPress={async () => {
              const clipboardText = await getClipboard();
              if (!!clipboardText.length) {
                onUrlChange(clipboardText);
              }
            }}
          >
            <ClipboardIcon />
          </IconButton>
        </View>
      ),
    },
    {
      label: t("propertyCreation.methods.uploadDocument"),
      value: InputMethod.Files,
    },
  ];

  return (
    <View>
      {!noCaption && (
        <Typography variant="caption">
          {t("propertyCreation.methods.selectMethod")}
        </Typography>
      )}
      <RadioButtonGroup
        values={selectValues}
        selectedValue={selectedInputMethod}
        onSelect={(value) => {
          if (value !== InputMethod.Url) {
            inputRef.current?.blur();
          }
          onInputMethodChange(value);
        }}
      />
    </View>
  );
};
